import uniqueId from 'lodash/uniqueId';
import { useGetCountryOption } from '../../hooks/useCountryOption';
import { useCallback } from 'react';
import { bboxToGeolocation, geolocationToBBox } from '../../helpers/geolocation.helpers';
import { LocationModel } from 'src/models/locations/location.model';
import {
    LocationInnerFormData,
    PeliasFormData,
    PeliasFormDataWithId,
    PeliasFormMeta,
} from './location-form.models';

export const usePeliusAdaptors = () => {
    const getCountryOption = useGetCountryOption();

    const peliasToLocationAdaptor = useCallback(
        (pelias: PeliasFormData) => {
            const countryOption = getCountryOption(pelias.countryCode);

            const bbox =
                pelias.street?.bbox ||
                pelias.postcode?.bbox ||
                pelias.city?.bbox ||
                pelias.region?.bbox;

            const location: LocationModel = {
                countryCode: pelias.countryCode,
                city: pelias.city?.label,
                postcode: pelias.postcode?.label,
                region: pelias.region?.label,
                street: pelias.street?.label,
                geolocation: bbox ? bboxToGeolocation(bbox) : countryOption?.geolocation,
            };
            return location;
        },
        [getCountryOption]
    );

    const toMeta = useCallback(
        (label: string | undefined): PeliasFormMeta | null =>
            label
                ? {
                      creation: 'converted from location',
                      label,
                  }
                : null,
        []
    );

    const multiLocationToPeliasAdaptor = useCallback(
        (locations: LocationModel[] | undefined) => {
            return (locations || [])
                .map(location => {
                    const convertedBbox = location.geolocation
                        ? geolocationToBBox(location.geolocation)
                        : undefined;

                    const pelias: PeliasFormDataWithId = {
                        id: uniqueId(),
                        countryCode: location.countryCode!,
                        city: toMeta(location.city),
                        postcode: toMeta(location.postcode),
                        region: toMeta(location.region),
                        street: toMeta(location.street),
                        convertedBbox,
                    };
                    return pelias;
                })
                .reduce<LocationInnerFormData>((acc, pelias) => {
                    acc[pelias.id] = pelias;
                    return acc;
                }, {});
        },
        [toMeta]
    );

    const singleLocationToPeliasAdaptor = useCallback(
        (id: string, defaultCountryCode: string, location: LocationModel | undefined) => {
            const convertedBbox = location?.geolocation
                ? geolocationToBBox(location.geolocation)
                : undefined;

            return {
                id,
                countryCode: location?.countryCode || defaultCountryCode,
                city: toMeta(location?.city),
                postcode: toMeta(location?.postcode),
                region: toMeta(location?.region),
                street: toMeta(location?.street),
                convertedBbox,
            } satisfies PeliasFormDataWithId;
        },
        [toMeta]
    );

    return {
        peliasToLocationAdaptor,
        multiLocationToPeliasAdaptor,
        singleLocationToPeliasAdaptor,
    };
};
